import React, { useEffect, useRef, useState } from 'react';
import {
    GameResultOverlay,
    GameShell,
} from '../shared';
import VoxelShapeCanvas from './turn-match/VoxelShapeCanvas';
import { createVoxelTurnRound } from './turn-match/voxelRound';
import { formatGameCopy } from './gameCopy';

function TurnMatchGame({
    copy = {},
    initialLevel = 1,
    onRunComplete = null,
    roundLimit = 18,
    showResults = true,
    ...props
}) {
    const startDifficulty = Math.max(1, Math.min(6, Math.round(Number(initialLevel) || 1)));
    const runSeedRef = useRef(Date.now());
    const nextDifficultyRef = useRef(startDifficulty);
    const completionReportedRef = useRef(false);
    const [round, setRound] = useState(1);
    const [difficulty, setDifficulty] = useState(startDifficulty);
    const [trial, setTrial] = useState(() => createVoxelTurnRound(1, runSeedRef.current, startDifficulty));
    const [choice, setChoice] = useState('');
    const [score, setScore] = useState(0);
    const [streak, setStreak] = useState(0);
    const [bestStreak, setBestStreak] = useState(0);
    const maxRounds = Math.max(1, Math.round(Number(roundLimit) || 18));
    const maxDifficulty = 6;
    const done = round > maxRounds;

    const reset = () => {
        completionReportedRef.current = false;
        runSeedRef.current = Date.now();
        nextDifficultyRef.current = startDifficulty;
        setRound(1);
        setDifficulty(startDifficulty);
        setTrial(createVoxelTurnRound(1, runSeedRef.current, startDifficulty));
        setChoice('');
        setScore(0);
        setStreak(0);
        setBestStreak(0);
    };

    const chooseOption = (optionId) => {
        if (done || choice) {
            return;
        }

        const correct = optionId === trial.correctId;
        const nextStreak = correct ? streak + 1 : 0;
        const shouldLevelUp = correct && nextStreak > 0 && nextStreak % 2 === 0;
        nextDifficultyRef.current = correct
            ? Math.min(maxDifficulty, difficulty + (shouldLevelUp ? 1 : 0))
            : Math.max(1, difficulty - 1);
        setScore((value) => value + (correct ? 1 : 0));
        setStreak(nextStreak);
        setBestStreak((value) => Math.max(value, nextStreak));
        setChoice(optionId);
    };

    const nextRound = () => {
        const nextRoundValue = round + 1;
        setRound(nextRoundValue);
        setChoice('');
        if (nextRoundValue <= maxRounds) {
            const nextDifficulty = nextDifficultyRef.current;
            setDifficulty(nextDifficulty);
            setTrial(createVoxelTurnRound(nextRoundValue, runSeedRef.current, nextDifficulty));
        }
    };

    useEffect(() => {
        if (!choice || done) {
            return undefined;
        }

        const timer = window.setTimeout(() => {
            nextRound();
        }, 700);

        return () => window.clearTimeout(timer);
    }, [choice, done]);

    useEffect(() => {
        if (!done || completionReportedRef.current) {
            return;
        }

        completionReportedRef.current = true;
        onRunComplete?.({
            level: difficulty,
            maxScore: maxRounds,
            score,
            streak: bestStreak,
        });
    }, [bestStreak, difficulty, done, maxRounds, onRunComplete, score]);

    const correctChoice = choice === trial.correctId;
    const streakLabel = formatGameCopy(copy.streak, { count: streak });

    return (
        <GameShell
            {...props}
            copy={copy}
            metrics={{ level: difficulty, maxScore: maxRounds, score, streak: bestStreak }}
            tone={correctChoice || done ? 'good' : choice ? 'miss' : 'ready'}
        >
            <div className="UFOV-brain-games__turn-game">
                <div className="UFOV-brain-games__streak-number" aria-label={streakLabel}>{streak}</div>
                <div className="UFOV-brain-games__turn-source" aria-label={copy.shapeToMatch}>
                    <VoxelShapeCanvas label="reference" voxels={trial.reference} />
                </div>
                <div className="UFOV-brain-games__turn-cue">{copy.turnMatchCue}</div>
                <div className="UFOV-brain-games__turn-options">
                    {trial.options.map((option, index) => {
                        const picked = choice === option.id;
                        const correct = choice && option.id === trial.correctId;

                        return (
                            <button
                                aria-label={formatGameCopy(copy.shapeOption, { number: index + 1 })}
                                className={`UFOV-brain-games__turn-option ${picked ? 'is-picked' : ''} ${correct ? 'is-correct' : ''}`.trim()}
                                disabled={done || Boolean(choice)}
                                key={`${trial.id}-${option.id}`}
                                onClick={() => chooseOption(option.id)}
                                type="button"
                            >
                                <VoxelShapeCanvas label={`option-${index + 1}`} voxels={option.voxels} />
                            </button>
                        );
                    })}
                </div>
            </div>
            {done && showResults ? (
                <GameResultOverlay metrics={{ maxScore: maxRounds, score, streak: bestStreak }} onRestart={reset} />
            ) : null}
        </GameShell>
    );
}

export default TurnMatchGame;
